import type { ComponentType } from 'react';
import type { AssistantAction, AssistantIdKind, AssistantView, AssistantViewKind } from '../../types/assistant';

/** What every assistant view component receives. */
export interface AssistantViewProps<TData = unknown> {
  view: AssistantView<TData>;
  /**
   * Runs an action from the view or one of its items through the action registry.
   * `item` is the list item it came from, when there is one.
   */
  onAction: (action: AssistantAction, item?: unknown) => void;
  /** True when the view shows inside the chat thread rather than the panel. */
  compact?: boolean;
}

export type AssistantViewComponent<TData = unknown> = ComponentType<AssistantViewProps<TData>>;

/** What the view tells the next turn about itself: the real ids it shows, by kind, sent as screen context. */
export interface AssistantViewOptions<TData = unknown> {
  /**
   * Reads the view's `data` (unchecked JSON from the server) into `TData`, or null when it is not readable; the panel
   * then shows a fallback instead of the component. A domain that types `TData` must give this, so the type is earned
   * at the boundary rather than cast; without it the component receives the raw data as `unknown`.
   */
  read?: (raw: unknown) => TData | null;
  /** The real ids the view shows, by kind, for the next turn's screen context. Called with the view as `read` gave it. */
  contextIds?: (view: AssistantView<TData>) => Partial<Record<AssistantIdKind, string[]>>;
}

const views = new Map<string, AssistantViewComponent>();
const options = new Map<string, AssistantViewOptions>();

/**
 * Registers the component that renders views of `kind`. Domains call this from their own file
 * under `views/<domain>/`, imported once from `views/index.ts`. A second registration for the same
 * kind replaces the first (hot reload), and says so in development.
 */
export function registerAssistantView<TData>(
  kind: AssistantViewKind,
  component: AssistantViewComponent<TData>,
  viewOptions?: AssistantViewOptions<TData>
): void {
  if (import.meta.env.DEV && views.has(kind) && views.get(kind) !== component) {
    console.warn(`Assistant view "${kind}" registered twice; the last one wins.`);
  }
  views.set(kind, component as AssistantViewComponent);
  if (viewOptions) options.set(kind, viewOptions as AssistantViewOptions);
  else options.delete(kind);
}

/** The ids a view of `kind` says it shows, for the next turn's screen context. Empty when it says nothing. */
export function assistantViewContextIds(view: AssistantView): Partial<Record<AssistantIdKind, string[]>> {
  try {
    const readable = readAssistantView(view);
    return readable ? (options.get(view.kind)?.contextIds?.(readable) ?? {}) : {};
  } catch {
    // A view whose data is not the shape its domain expects tells the next turn nothing rather than failing it.
    return {};
  }
}

/**
 * The view as its component should get it: `data` through the kind's `read` when it has one. Null when `read` refused
 * the data (or threw), so the panel shows a fallback rather than a component working on the wrong shape.
 */
export function readAssistantView(view: AssistantView): AssistantView | null {
  const read = options.get(view.kind)?.read;
  if (!read) return view;
  try {
    const data = read(view.data);
    return data === null || data === undefined ? null : { ...view, data };
  } catch {
    return null;
  }
}

/** The component for `kind`, or undefined when no domain registered one (the panel shows a fallback). */
export function getAssistantView(kind: AssistantViewKind): AssistantViewComponent | undefined {
  return views.get(kind);
}

/** Test only: forget every registration. */
export function resetAssistantViewsForTest(): void {
  views.clear();
  options.clear();
}
